import type { Table } from '@tanstack/react-table';
import { CheckIcon, Download, Trash2 } from 'lucide-react';
import * as React from 'react';

import { exportTableToCSV } from '@/lib/export';

import {
  DataTableActionBar,
  DataTableActionBarAction,
  DataTableActionBarSelection,
} from '@/components/dataTable/ActionBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDynamicLocale } from '@/hoslog-intl';
import { MonthlyReportData } from '@/services/payment/payment.types';
import { Separator } from 'ui/separator';
import { ApproveAllConfirmationDialog } from './confirmation/approveAll';

type Action = 'approve' | 'export' | 'delete';

interface MonthlyReportTableActionBarProps {
  table?: Table<MonthlyReportData>;
}

export function MonthlyReportTableActionBar({ table }: MonthlyReportTableActionBarProps) {
  if (!table) {
    throw new Error('either pass a `table` instance or use it inside DataTable.');
  }
  const rows = table.getFilteredSelectedRowModel().rows;
  const { t } = useDynamicLocale();
  const [isPending, startTransition] = React.useTransition();
  const [currentAction, setCurrentAction] = React.useState<Action | null>(null);

  const getIsActionPending = React.useCallback(
    (action: Action) => isPending && currentAction === action,
    [isPending, currentAction],
  );

  const onExport = React.useCallback(() => {
    setCurrentAction('export');
    startTransition(() => {
      exportTableToCSV(table, {
        excludeColumns: ['select', 'actions'],
        onlySelected: true,
      });
    });
  }, [table]);

  const onDelete = React.useCallback(() => {
    setCurrentAction('delete');
    startTransition(() => {
      table.toggleAllRowsSelected(false);
    });
  }, [table]);

  const { openConfirmation } = useConfirmationStore();

  const onAddnew = () => {
    openConfirmation({
      title: t('common.page.modal.confirmation.title'),
      description: t('paymentManagement.affiliatePaymentLogs.monthlyReport.modal.approveAllDescription'),

      cancelLabel: t('common.page.modal.no'),
      content: <ApproveAllConfirmationDialog selectedData={rows.map((row) => row.original)} />,
      actionLabel: t('common.page.modal.yes'),
      onAction: () => {},
      onCancel: () => {},
      size: 'lg',
      variant: 'default',
    });
  };

  return (
    <DataTableActionBar table={table} visible={rows.length > 0}>
      <DataTableActionBarSelection
        table={table}
        selectedLabel={t('common.page.table.selected')}
        clearSelectionLabel={t('common.page.table.clearSelection')}
        escapeLabel={t('common.page.keyboard.escape')}
      />
      <Separator orientation="vertical" className="hidden data-[orientation=vertical]:h-5 sm:block" />
      <div className="flex items-center gap-1.5">
        <DataTableActionBarAction
          size="icon"
          tooltip={t('common.page.table.exportCSV')}
          isPending={getIsActionPending('export')}
          onClick={onExport}
        >
          <Download />
        </DataTableActionBarAction>
        <DataTableActionBarAction
          size="icon"
          tooltip={t('common.page.button.approve')}
          isPending={getIsActionPending('approve')}
          onClick={onAddnew}
        >
          <CheckIcon />
        </DataTableActionBarAction>
        <DataTableActionBarAction
          size="icon"
          tooltip={t('common.page.table.clearSelection')}
          isPending={getIsActionPending('delete')}
          onClick={onDelete}
        >
          <Trash2 />
        </DataTableActionBarAction>
      </div>
    </DataTableActionBar>
  );
}
